/**
 * FuzzyCompanySuggestions
 *
 * Displays "Did you mean:" suggestions below the "Company name" text input
 * when the user has chosen "Can't find my referring company" and is typing.
 *
 * Fuzzy matching begins once the user has entered at least 2 characters.
 * Suggestions are rendered as pill chips in a wrapping row. Clicking a chip
 * calls onSelect with the matched company name so the caller can switch the
 * form back to a known company.
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '../ui';

// ---------------------------------------------------------------------------
// Application green palette (matches token defaults)
// ---------------------------------------------------------------------------
const GREEN_DARK  = '#003F44'; // primary.main
const GREEN_MID   = '#155F54'; // brand.main
const GREEN_LIGHT = '#4A8B7F'; // brand.light

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FuzzyCompanySuggestionsProps {
  /** Current text typed in the manual-entry field. */
  inputValue: string;
  /** The list of real company names to match against. */
  companyNames: string[];
  /** Called when the user clicks a suggestion chip. */
  onSelect: (companyName: string) => void;
  /** Maximum number of suggestions to display (default: 5). */
  maxSuggestions?: number;
}

// ---------------------------------------------------------------------------
// Edit distance (Optimal String Alignment / restricted Damerau–Levenshtein)
//
// Unlike plain Levenshtein this counts a transposition (adjacent character
// swap) as a single operation, so "re" → "er" costs 1, not 2.
// This is important for real-world typos like "utikities" → "utilities".
// ---------------------------------------------------------------------------
function editDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;

  // Allocate rows only once for space efficiency
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,           // deletion
        dp[i][j - 1] + 1,           // insertion
        dp[i - 1][j - 1] + cost     // substitution
      );
      // Transposition: swap of two adjacent characters
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        dp[i][j] = Math.min(dp[i][j], dp[i - 2][j - 2] + cost);
      }
    }
  }
  return dp[m][n];
}

// ---------------------------------------------------------------------------
// Word-level similarity
//
// Returns [0, 1] for a single query word against a single candidate word.
// ---------------------------------------------------------------------------
function wordSimilarity(qWord: string, cWord: string): number {
  if (qWord === cWord) return 1.0;

  // Prefix: candidate starts with the query word (user still typing this word)
  if (cWord.startsWith(qWord)) {
    // Score scales up with how much of the candidate word is already covered
    return 0.80 + 0.18 * (qWord.length / cWord.length);
  }

  const maxLen = Math.max(qWord.length, cWord.length);
  const dist   = editDistance(qWord, cWord);
  const sim    = 1 - dist / maxLen;

  // Require at least 55 % similarity; scale fuzzy matches slightly below prefix/exact
  return sim >= 0.55 ? sim * 0.95 : 0;
}

// ---------------------------------------------------------------------------
// Main fuzzy scorer
//
// Strategy:
//  1. Fast-path: exact substring (already handled well by simple check).
//  2. Word-level matching with edit distance + transpositions:
//     - Split both strings into words.
//     - For every query word, find the best-matching candidate word.
//     - Special handling for the last query word when it is short (≤ 2 chars)
//       — this is often an incomplete word the user hasn't finished typing.
//       If no candidate word starts with it, exclude it from scoring entirely
//       rather than penalising the result.
//  3. Final score = totalWordScore / effectiveQueryWordCount.
// ---------------------------------------------------------------------------

const MIN_WORD_SCORE = 0.55;   // minimum per-word similarity to count as a match
const TRAILING_WORD_MAX = 2;   // a trailing word this short is treated as partial

function fuzzyScore(query: string, candidate: string): number {
  const q = query.trim().toLowerCase();
  const c = candidate.trim().toLowerCase();
  if (!q || !c) return 0;

  // --- Fast path: exact substring ---
  if (c.includes(q)) {
    // Reward word-boundary starts more than mid-word occurrences
    const atBoundary = new RegExp(`\\b${escapeRegex(q)}`).test(c);
    return atBoundary ? 0.95 : 0.87;
  }

  // --- Word-level matching ---
  const qWords = q.split(/\s+/);
  const cWords = c.split(/\s+/);

  let totalScore      = 0;
  let effectiveCount  = 0; // how many query words actually count toward the score

  for (let qi = 0; qi < qWords.length; qi++) {
    const qWord    = qWords[qi];
    const isLast   = qi === qWords.length - 1;
    const isShort  = qWord.length <= TRAILING_WORD_MAX;

    // Short trailing word: check for prefix hints in the candidate.
    // If none found, skip it entirely — the user hasn't finished typing it.
    if (isLast && isShort) {
      const prefixHit = cWords.some((cw) => cw.startsWith(qWord));
      if (prefixHit) {
        // Give a partial bonus proportional to prefix coverage
        const bestPrefixScore = Math.max(
          ...cWords.map((cw) => (cw.startsWith(qWord) ? 0.6 * (qWord.length / cw.length) + 0.4 : 0))
        );
        totalScore += bestPrefixScore;
        effectiveCount++;
      }
      // If no prefix match, just don't count this word at all
      continue;
    }

    // Regular word: find the best-matching candidate word
    let bestScore = 0;
    for (const cWord of cWords) {
      const score = wordSimilarity(qWord, cWord);
      if (score > bestScore) bestScore = score;
    }

    totalScore += bestScore;
    effectiveCount++;
  }

  if (effectiveCount === 0) return 0;
  return totalScore / effectiveCount;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Minimum overall score threshold to surface a suggestion */
const MIN_SCORE = 0.40;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FuzzyCompanySuggestions: React.FC<FuzzyCompanySuggestionsProps> = ({
  inputValue,
  companyNames,
  onSelect,
  maxSuggestions = 5,
}) => {
  const suggestions = useMemo<string[]>(() => {
    const q = inputValue?.trim() ?? '';
    if (q.length < 2) return [];

    return companyNames
      .map((name) => ({ name, score: fuzzyScore(q, name) }))
      .filter((item) => item.score >= MIN_SCORE)
      .sort((a, b) => b.score - a.score)
      .slice(0, maxSuggestions)
      .map((item) => item.name);
  }, [inputValue, companyNames, maxSuggestions]);

  if (suggestions.length === 0) return null;

  return (
    <Box
      data-testid="components-fuzzy-company-suggestions-container"
      sx={{
        mt: 1.5,
        px: 1.5,
        pt: 1.25,
        pb: 1.5,
        borderRadius: 2,
        border: `1px solid ${GREEN_LIGHT}40`,
        backgroundColor: `${GREEN_DARK}08`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 1,
          fontWeight: 600,
          color: GREEN_MID,
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
          fontSize: '0.68rem',
        }}
      >
        Did you mean:
      </Typography>

      {/* Chips wrap onto new lines */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
        }}
      >
        {suggestions.map((name) => (
          <Box
            key={name}
            component="button"
            type="button"
            data-testid={`components-fuzzy-company-suggestions-chip-${name.replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => onSelect(name)}
            sx={{
              // Reset button styles
              fontFamily: 'inherit',
              cursor: 'pointer',
              outline: 'none',
              // Chip appearance
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.5,
              py: 0.5,
              borderRadius: '20px',
              border: `1.5px solid ${GREEN_LIGHT}`,
              backgroundColor: '#ffffff',
              color: GREEN_DARK,
              fontSize: '0.8125rem',
              fontWeight: 500,
              lineHeight: 1.4,
              transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
              '&:hover': {
                backgroundColor: GREEN_DARK,
                borderColor: GREEN_DARK,
                color: '#ffffff',
              },
              '&:active': {
                backgroundColor: GREEN_MID,
                borderColor: GREEN_MID,
                color: '#ffffff',
              },
              '&:focus-visible': {
                boxShadow: `0 0 0 2px ${GREEN_LIGHT}`,
              },
            }}
          >
            {name}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default FuzzyCompanySuggestions;
